// Extracted out of connect-tab.js (fix round 1, Finding 1). `apiMessage` used
// to be exported from there purely so connect-support-block.js could reuse it
// instead of reinventing it, which made connect-tab.js and
// connect-support-block.js import each other — safe today only because both
// sides defer their use of the other's export to inside a function body, not
// module-evaluation time. That "safe until someone moves a call to module
// scope" fragility has no upside once pulling this out costs fifteen lines,
// so it lives here instead: a leaf both files can import without importing
// each other. Behaviour is unchanged — see each piece's own comment below,
// carried over verbatim.
//
// Moved again (Part B7) from company-detail/tabs to views/connect once
// use-account-session.js needed the same extraction: that hook is shared by
// restaurant surfaces (settings/payments, connect/finance) as well as the
// admin support block, so it cannot reach into company-detail/tabs for it.
// views/connect is already the shared ancestor company-detail/tabs imports
// connect-requirements.js, use-account-session.js and the finance helpers
// from — this file now follows that same convention instead of running the
// dependency the other way.

// The one place an error's server-supplied message is read. Shared by
// connect-tab.js's toast (`notifyError`) and its inline "couldn't read the
// status" copy so the two never disagree about what went wrong, and now by
// connect-support-block.js's own balance-read failure for the same reason.
//
// ConnectController answers its failures with a bare JSON string body —
// `UnprocessableEntity("User role / permission required")`,
// `NotFound("Company not found")` — not the `{ Message }` envelope the rest
// of this API wraps errors in, so `error.response.data` is itself the
// message on this controller. Same idiom notification.js's
// extractErrorMessage already uses for the same reason.
// A 5xx (or a proxy error page) can put a dashboard.stripe.com URL, or a raw
// stack trace, straight into a server-supplied error body. This board
// forbids linking a restaurant to the Stripe dashboard anywhere on this
// screen (see connect-tab.js's onCopyLink comment), and a stack trace is not
// copy written for an admin to read either way — so both are
// stripped/rejected here, in the one place a server message is read, rather
// than trusted to every call site that happens to render one.
const DASHBOARD_URL_RE = /https?:\/\/dashboard\.stripe\.com\S*/gi;
// What a stripped URL is replaced with, never simply deleted — `\S*` also
// swallows the URL's trailing punctuation, and removing the whole span left a
// hole where the surrounding words closed up on each other ("Retry failed.
// See for details."). A marker keeps the sentence parsing as a sentence.
const STRIPPED_URL_MARKER = '[link removed]';

// A genuine stack frame's parenthesised location carries a line (and usually
// a column) number — "(/app/server.js:12:3)", "(node:internal/...:1105:14)"
// — which ordinary prose that merely contains the words "at X (Y)" does not.
// Requiring that digit:digit (or bare digit) after the colon is what tells
// "at Object.<anonymous> (/app/server.js:12:3)" apart from a sentence like
// "Reader rejected at Store (ABC) - retry", which used to blank the entire
// message for containing nothing more alarming than a store name in
// parentheses. Multi-line traces are already caught by the `\n` check below
// regardless of this pattern.
const looksLikeStackTrace = (text) =>
  /\n/.test(text) || /\bat\s+\S+\s*\([^()]*:\d+(?::\d+)?\)/.test(text) || text.length > 300;

export const sanitizeServerMessage = (raw) => {
  if (!raw) return '';
  const stripped = raw
    .replace(DASHBOARD_URL_RE, STRIPPED_URL_MARKER)
    .replace(/\s{2,}/g, ' ')
    .trim();
  if (!stripped || looksLikeStackTrace(stripped)) return '';
  return stripped;
};

export const apiMessage = (error) => {
  const data = error?.response?.data;
  const raw = data?.Message || data?.message || (typeof data === 'string' ? data : '') || '';
  // `data?.Message`/`data?.message` are strings only by convention — a 503
  // status code, an object, or an array is just as legal a value there, and
  // every downstream string op (sanitizeServerMessage's own `.replace`s
  // included) throws on anything but a string. That throw used to unwind
  // from inside the catch block handling the ORIGINAL error, which is worse
  // than losing this one: see this function's own callers for what a thrown
  // apiMessage does to each of them. A raw non-string code is not prose worth
  // sanitising and showing an admin anyway, so it is treated the same as no
  // message at all, and every caller already has a fallback sentence for that.
  return sanitizeServerMessage(typeof raw === 'string' ? raw : '');
};
